import {
  Form,
  Input,
  Button,
  Table,
  Space,
  Popconfirm,
  message,
  Switch,
  Modal,
  Alert,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useCallback, useMemo, useState, useEffect } from 'react';
import dayjs from 'dayjs';

import SearchForm, { SearchFormItem } from '@/components/SearchForm';
import { useTable } from '@/hooks/useTable';
import { FORMAT_TIME } from '@/types/common';
import {
  getVersionList,
  VersionItem,
  VersionListParams,
  deleteVersion,
  queryAppVersionSwitch,
  updateAppVersionSwitch,
  queryAppVersionSwitchAndroid,
  updateAppVersionSwitchAndroid,
  queryRegisterMemberPopup,
  updateRegisterMemberPopup,
} from '@/api/version';
import { MESSAGES, DEFAULT_SWITCH_STATUS } from './constants';
import VersionDrawer from './VersionDrawer';
import AppStoreInfo from './AppStoreInfo';

const APP_STORE_URL =
  'https://apps.apple.com/jp/app/suppart/id6739333297?l=en-US';

interface SwitchState {
  status: number;
  updateTime?: string;
  updateUser?: string;
}

/**
 * 版本管理页面
 * 包含版本列表展示、版本开关控制、版本的增删改查等功能
 */
const VersionPage = ({ systemPlatform = null }) => {
  const [form] = Form.useForm<{ versionNumber?: string }>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<VersionItem>();
  const [switchState, setSwitchState] = useState<SwitchState>({
    status: DEFAULT_SWITCH_STATUS,
  });
  const [popupState, setPopupState] = useState<SwitchState>({
    status: DEFAULT_SWITCH_STATUS,
  });
  const [messageApi, contextHolder] = message.useMessage();
  const popupCode = systemPlatform
    ? 'registerMemberPopupAndroid'
    : 'registerMemberPopupIos';

  const handleError = useCallback(
    (error: any, errorMessage: string) => {
      console.error(errorMessage, error);
      messageApi.error(errorMessage);
    },
    [messageApi]
  );

  /**
   * 获取版本开关状态
   * 包括开关状态、最后更新时间和更新人
   */
  const fetchSwitchStatus = useCallback(async () => {
    try {
      const fn = systemPlatform
        ? queryAppVersionSwitchAndroid
        : queryAppVersionSwitch;
      const resp = await fn();
      if (resp.status === 200) {
        const { data = {} } = resp.data || {};
        setSwitchState({
          status: data.status,
          updateTime: data.updateTime,
          updateUser: data.updateUserName || '-',
        });
      }
    } catch (error) {
      handleError(error, MESSAGES.ERROR_MESSAGES.FETCH_SWITCH);
    }
  }, [handleError, systemPlatform]);

  useEffect(() => {
    fetchSwitchStatus();
  }, [fetchSwitchStatus]);

  /** 注册后会员弹窗开关状态 */
  const fetchPopupStatus = useCallback(async () => {
    try {
      const resp = await queryRegisterMemberPopup(popupCode);
      if (resp.status === 200) {
        const { data = {} } = resp.data || {};
        setPopupState({
          status: data.status,
          updateTime: data.updateTime,
          updateUser: data.updateUserName || '-',
        });
      }
    } catch (error) {
      handleError(error, '获取注册后会员弹窗开关失败');
    }
  }, [handleError, popupCode]);

  useEffect(() => {
    fetchPopupStatus();
  }, [fetchPopupStatus]);

  const handlePopupChange = useCallback(
    async (checked: boolean) => {
      try {
        const resp = await updateRegisterMemberPopup(
          popupCode,
          checked ? 1 : 0
        );
        if (resp.status === 200) {
          messageApi.success(
            checked ? '已开启注册后会员弹窗' : '已关闭注册后会员弹窗'
          );
          fetchPopupStatus();
        }
      } catch (error) {
        handleError(error, '修改注册后会员弹窗开关失败');
      }
    },
    [fetchPopupStatus, handleError, messageApi, popupCode]
  );

  const handleSwitchChange = useCallback(
    (checked: boolean) => {
      const newStatus = checked ? 1 : 0;
      const confirmConfig = !checked
        ? null
        : {
            title: MESSAGES.SWITCH_CONFIRM_TITLE,
            content: MESSAGES.SWITCH_CONFIRM_CONTENT,
          };
      const updateSwitch = async () => {
        try {
          const fn = systemPlatform
            ? updateAppVersionSwitchAndroid
            : updateAppVersionSwitch;
          const resp = await fn(newStatus);
          if (resp.status === 200) {
            messageApi.success(
              checked
                ? MESSAGES.SUCCESS_MESSAGES.SWITCH_ON
                : MESSAGES.SUCCESS_MESSAGES.SWITCH_OFF
            );
            fetchSwitchStatus();
          }
        } catch (error) {
          handleError(error, MESSAGES.ERROR_MESSAGES.SWITCH);
        }
      };

      if (confirmConfig) {
        Modal.confirm({
          ...confirmConfig,
          onOk: updateSwitch,
        });
      } else {
        updateSwitch();
      }
    },
    [fetchSwitchStatus, handleError, messageApi]
  );

  const {
    data,
    total,
    currentPage,
    pageSize,
    isLoading,
    refresh,
    updateParams,
    handleTableChange,
  } = useTable<VersionItem, VersionListParams>({
    queryKey: `versionList-${systemPlatform ?? 'ios'}`,
    queryFn: async (page, pageSize) => {
      const resp = await getVersionList({
        pageSize,
        currentPage: page,
        systemPlatform,
      });
      return resp.data;
    },
  });

  const handleEdit = useCallback((record: VersionItem) => {
    setEditingVersion(record);
    setIsDrawerOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        const resp = await deleteVersion(id);
        if (resp.status === 200) {
          messageApi.success(MESSAGES.SUCCESS_MESSAGES.DELETE);
          refresh();
        } else {
          messageApi.error(resp.message || MESSAGES.ERROR_MESSAGES.DELETE);
        }
      } catch (error) {
        handleError(error, MESSAGES.ERROR_MESSAGES.DELETE);
      }
    },
    [handleError, messageApi, refresh]
  );

  const columns = useMemo(
    () => [
      {
        title: '版本号',
        dataIndex: 'versionNumber',
        key: 'versionNumber',
      },
      {
        title: '版本内容',
        dataIndex: 'versionContent',
        key: 'versionContent',
      },
      {
        title: '强制更新',
        dataIndex: 'versionUpdate',
        key: 'versionUpdate',
        render: (value: number) => (value === 1 ? '是' : '否'),
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        render: (value: string) => dayjs(value).format(FORMAT_TIME),
      },
      {
        title: '创建人',
        dataIndex: 'createUserName',
        key: 'createUserName',
      },
      {
        title: '更新时间',
        dataIndex: 'updateTime',
        key: 'updateTime',
        render: (value: string) => dayjs(value).format(FORMAT_TIME),
      },
      {
        title: '更新人',
        dataIndex: 'updateUserName',
        key: 'updateUserName',
      },
      {
        title: '操作',
        key: 'action',
        fixed: 'right',
        render: (_: any, record: VersionItem) => (
          <Space size="middle">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              编辑
            </Button>
            <Popconfirm
              title={MESSAGES.DELETE_CONFIRM}
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleDelete, handleEdit]
  );

  const handleDrawerSuccess = useCallback(() => {
    refresh();
    setIsDrawerOpen(false);
    setEditingVersion(undefined);
  }, [refresh]);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingVersion(undefined);
  }, []);

  // TODO: 搜索功能暂时隐藏，后续需要时再启用
  const handleSearch = useCallback(() => {
    const values = form.getFieldsValue();
    updateParams(values);
  }, [form, updateParams]);

  const searchFormItems: SearchFormItem[] = [
    {
      name: 'versionNumber',
      label: '版本号',
      component: <Input />,
    },
  ];

  return (
    <div>
      {contextHolder}

      {/* <SearchForm form={form} items={searchFormItems} onFinish={handleSearch} /> */}

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {!systemPlatform && <AppStoreInfo appUrl={APP_STORE_URL} />}
        <Space size="middle" align="start">
          <Button type="primary" onClick={() => setIsDrawerOpen(true)}>
            新增版本
          </Button>
          <div>
            <Space>
              版本开关：
              <Switch
                checked={switchState.status === 1}
                onChange={handleSwitchChange}
                checkedChildren="开启"
                unCheckedChildren="关闭"
              />
            </Space>
            <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
              最后{switchState.status === 1 ? '开启' : '关闭'}时间：
              {switchState.updateTime
                ? dayjs(switchState.updateTime).format(FORMAT_TIME)
                : '-'}
              {switchState.updateUser ? ` (${switchState.updateUser})` : ''}
            </div>
          </div>
          <div>
            <Space>
              注册后会员弹窗：
              <Switch
                checked={popupState.status === 1}
                onChange={handlePopupChange}
                checkedChildren="弹出"
                unCheckedChildren="不弹"
              />
            </Space>
            <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
              关闭=新用户注册后不自动弹出会员页（仅 iOS 3.2.6+/新版 Android
              生效）
            </div>
            <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
              最后{popupState.status === 1 ? '开启' : '关闭'}时间：
              {popupState.updateTime
                ? dayjs(popupState.updateTime).format(FORMAT_TIME)
                : '-'}
              {popupState.updateUser ? ` (${popupState.updateUser})` : ''}
            </div>
          </div>
        </Space>

        {switchState.status === 0 && (
          <Alert message={MESSAGES.SWITCH_WARNING} type="warning" showIcon />
        )}

        <Table
          columns={columns}
          dataSource={data}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
        />
      </Space>

      <VersionDrawer
        visible={isDrawerOpen}
        systemPlatform={systemPlatform}
        editData={editingVersion}
        onClose={handleDrawerClose}
        onSuccess={handleDrawerSuccess}
      />
    </div>
  );
};

export default VersionPage;
